// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { useAuth } from '../AuthContext';

function AnalyticsDashboardPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('30d');

  const overviewStats = [
    { label: 'Website Visitors', value: '8,452', change: '+12.3%', color: '#6366f1' },
    { label: 'Ad Spend', value: '$3,240', change: '-5.2%', color: '#ef4444' },
    { label: 'Email Subscribers', value: '2,847', change: '+4.8%', color: '#10b981' },
    { label: 'Social Followers', value: '10.5K', change: '+8.1%', color: '#3b82f6' },
    { label: 'Leads Generated', value: '142', change: '+22.4%', color: '#8b5cf6' },
    { label: 'Revenue', value: '$24,680', change: '+15.7%', color: '#f59e0b' },
  ];

  const websiteData = {
    pageViews: '24,312', bounceRate: '42.1%', avgSession: '3:24', topPages: [
      { page: '/home', views: 8420, bounce: '38%' }, { page: '/services', views: 4210, bounce: '41%' },
      { page: '/pricing', views: 3180, bounce: '35%' }, { page: '/about', views: 2890, bounce: '52%' },
      { page: '/contact', views: 2140, bounce: '28%' }, { page: '/blog', views: 1870, bounce: '55%' },
    ],
    sources: [
      { name: 'Organic Search', pct: 38, color: '#10b981' }, { name: 'Direct', pct: 24, color: '#6366f1' },
      { name: 'Social Media', pct: 18, color: '#3b82f6' }, { name: 'Paid Ads', pct: 12, color: '#f59e0b' },
      { name: 'Referral', pct: 5, color: '#8b5cf6' }, { name: 'Email', pct: 3, color: '#ec4899' },
    ],
    devices: [{ name: 'Desktop', pct: 52 }, { name: 'Mobile', pct: 41 }, { name: 'Tablet', pct: 7 }],
  };

  const adData = [
    { platform: 'Facebook', spend: '$1,240', impressions: '145K', clicks: '3.2K', ctr: '2.2%', cpa: '$4.12', roas: '3.8x' },
    { platform: 'Google', spend: '$890', impressions: '98K', clicks: '2.1K', ctr: '2.1%', cpa: '$5.60', roas: '4.2x' },
    { platform: 'Instagram', spend: '$650', impressions: '120K', clicks: '2.8K', ctr: '2.3%', cpa: '$3.25', roas: '3.1x' },
    { platform: 'LinkedIn', spend: '$460', impressions: '32K', clicks: '890', ctr: '2.8%', cpa: '$8.40', roas: '2.4x' },
  ];

  const seoData = [
    { keyword: 'professional services canada', position: 3, volume: '2.4K', change: '+2' },
    { keyword: 'business automation tools', position: 7, volume: '5.1K', change: '+5' },
    { keyword: 'custom app development alberta', position: 1, volume: '890', change: '0' },
    { keyword: 'forge standard platform', position: 1, volume: '210', change: '0' },
    { keyword: 'small business software canada', position: 12, volume: '8.2K', change: '+3' },
  ];

  const tabs = ['overview', 'website', 'ads', 'email', 'social', 'seo'];

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>{'\uD83D\uDCCA'} Analytics</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 15 }}>Unified view of all your business metrics.</p>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {['7d', '30d', '90d', '1yr'].map(r => (
            <button key={r} onClick={() => setDateRange(r)} style={{ padding: '6px 14px', background: dateRange === r ? '#6366f1' : 'var(--card)', color: dateRange === r ? '#fff' : 'var(--text-muted)', border: dateRange === r ? 'none' : '1px solid var(--border)', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>{r}</button>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button key={t} onClick={() => setActiveTab(t)} style={{ padding: '8px 20px', background: activeTab === t ? '#6366f1' : 'var(--card)', color: activeTab === t ? '#fff' : 'var(--text-muted)', border: activeTab === t ? 'none' : '1px solid var(--border)', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer', textTransform: 'capitalize' }}>{t}</button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16 }}>
          {overviewStats.map(s => (
            <div key={s.label} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>{s.label}</p>
              <p style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.value}</p>
              <p style={{ fontSize: 12, color: s.change.startsWith('+') ? '#10b981' : '#ef4444', marginTop: 4 }}>{s.change} vs last period</p>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'website' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 24 }}>
            <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Page Views</p><p style={{ fontSize: 28, fontWeight: 700, color: '#6366f1' }}>{websiteData.pageViews}</p>
            </div>
            <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Bounce Rate</p><p style={{ fontSize: 28, fontWeight: 700, color: '#f59e0b' }}>{websiteData.bounceRate}</p>
            </div>
            <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Avg Session</p><p style={{ fontSize: 28, fontWeight: 700, color: '#10b981' }}>{websiteData.avgSession}</p>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
            <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}><h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>Top Pages</h3></div>
              {websiteData.topPages.map(p => (
                <div key={p.page} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', padding: '12px 20px', borderBottom: '1px solid var(--border)', fontSize: 14 }}>
                  <span style={{ color: '#6366f1', fontWeight: 500 }}>{p.page}</span>
                  <span style={{ color: 'var(--text-muted)' }}>{p.views.toLocaleString()} views</span>
                  <span style={{ color: 'var(--text-muted)' }}>{p.bounce} bounce</span>
                </div>
              ))}
            </div>
            <div>
              <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 16 }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>Traffic Sources</h3>
                {websiteData.sources.map(s => (
                  <div key={s.name} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 13, color: 'var(--text)' }}>{s.name}</span>
                      <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{s.pct}%</span>
                    </div>
                    <div style={{ height: 6, background: 'var(--bg)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ width: `${s.pct}%`, height: '100%', background: s.color, borderRadius: 3 }} />
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>Devices</h3>
                {websiteData.devices.map(d => (
                  <div key={d.name} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 14, color: 'var(--text-muted)' }}>
                    <span>{d.name}</span><span>{d.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'ads' && (
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 100px 100px 80px 80px 80px 80px', padding: '12px 20px', background: 'var(--bg)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
            <span>Platform</span><span>Spend</span><span>Impressions</span><span>Clicks</span><span>CTR</span><span>CPA</span><span>ROAS</span>
          </div>
          {adData.map(a => (
            <div key={a.platform} style={{ display: 'grid', gridTemplateColumns: '1.5fr 100px 100px 80px 80px 80px 80px', padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 14, alignItems: 'center' }}>
              <span style={{ fontWeight: 500, color: 'var(--text)' }}>{a.platform}</span>
              <span style={{ color: 'var(--text-muted)' }}>{a.spend}</span>
              <span style={{ color: 'var(--text-muted)' }}>{a.impressions}</span>
              <span style={{ color: 'var(--text-muted)' }}>{a.clicks}</span>
              <span style={{ color: 'var(--text-muted)' }}>{a.ctr}</span>
              <span style={{ color: 'var(--text-muted)' }}>{a.cpa}</span>
              <span style={{ fontWeight: 600, color: '#10b981' }}>{a.roas}</span>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'email' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
          {[{ l: 'Subscribers', v: '2,847', c: '#6366f1' }, { l: 'Open Rate', v: '38.2%', c: '#10b981' }, { l: 'Click Rate', v: '12.4%', c: '#3b82f6' }, { l: 'Revenue', v: '$4,280', c: '#f59e0b' }].map(s => (
            <div key={s.l} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{s.l}</p><p style={{ fontSize: 28, fontWeight: 700, color: s.c }}>{s.v}</p>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'social' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
          {[{ l: 'Total Followers', v: '10.5K', c: '#6366f1' }, { l: 'Engagement Rate', v: '3.8%', c: '#10b981' }, { l: 'Posts This Month', v: '24', c: '#3b82f6' }, { l: 'Top Platform', v: 'Instagram', c: '#ec4899' }].map(s => (
            <div key={s.l} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{s.l}</p><p style={{ fontSize: 28, fontWeight: 700, color: s.c }}>{s.v}</p>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'seo' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16, marginBottom: 24 }}>
            {[{ l: 'Organic Traffic', v: '3,210', c: '#10b981' }, { l: 'Keywords Ranked', v: '47', c: '#6366f1' }, { l: 'Backlinks', v: '124', c: '#3b82f6' }, { l: 'Domain Authority', v: '38', c: '#8b5cf6' }].map(s => (
              <div key={s.l} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
                <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{s.l}</p><p style={{ fontSize: 28, fontWeight: 700, color: s.c }}>{s.v}</p>
              </div>
            ))}
          </div>
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '3fr 80px 100px 80px', padding: '12px 20px', background: 'var(--bg)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
              <span>Keyword</span><span>Position</span><span>Volume</span><span>Change</span>
            </div>
            {seoData.map(k => (
              <div key={k.keyword} style={{ display: 'grid', gridTemplateColumns: '3fr 80px 100px 80px', padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 14, alignItems: 'center' }}>
                <span style={{ color: 'var(--text)', fontWeight: 500 }}>{k.keyword}</span>
                <span style={{ fontWeight: 700, color: k.position <= 3 ? '#10b981' : k.position <= 10 ? '#f59e0b' : 'var(--text-muted)' }}>#{k.position}</span>
                <span style={{ color: 'var(--text-muted)' }}>{k.volume}/mo</span>
                <span style={{ color: k.change.startsWith('+') ? '#10b981' : 'var(--text-muted)' }}>{k.change === '0' ? '-' : k.change}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default AnalyticsDashboardPage;
